"use client";

import { AnimatePresence } from "framer-motion";
import { Icon, Text } from "@pamperedchef/design-system";

import { Button, type ButtonProps } from "@pamperedchef/design-system";
import { className as classNameHelper } from "@/app/utilities/className";
import React, { useEffect, useState } from "react";

import type { AddToCartActionRequest } from "@/app/actions/cart";
import { getCartUrl } from "@/app/actions/get-cart-url";
import { useSessionStore } from "@/app/store";

import { useAddToCart } from "./useAddToCart";
import { AddToCartSlider } from "../AddToCartSlider";
import * as styles from "./AddToCart.module.scss";

export interface AddToCartProps extends React.HTMLAttributes<HTMLDivElement> {
	buttonProps: ButtonProps;
	payload: AddToCartActionRequest;
	errorMessage: string | null;
	alreadyAddedMessage: string;
}

/**
 * Component to add items to the cart
 */
export function AddtoCart({
	payload,
	buttonProps,
	errorMessage,
	alreadyAddedMessage,
	className
}: AddToCartProps): JSX.Element {
	const { handleClick, loading, cartQuantity, error, submitted } =
		useAddToCart(payload);
	const sessionState = useSessionStore((state) => state.sessionState);
	const [isSliderOpen, setIsSliderOpen] = useState(false);

	async function handleViewCartAndCheckout(): Promise<void> {
		if (!sessionState?.sessionId) return;
		const url = await getCartUrl(sessionState.sessionId);
		window.location.href = url;
	}

	useEffect(() => {
		if (submitted) setIsSliderOpen(true);
	}, [submitted]);

	return (
		<div {...classNameHelper(styles["add-to-cart"], className)}>
			<AnimatePresence>
				<Button
					{...buttonProps}
					onClick={handleClick}
					data-loading={loading}
					data-submitted={submitted}
					motionProps={{
						initial: { x: 10, opacity: 0 },
						animate: { x: 0, opacity: 1 },
						exit: { x: -10, opacity: 0 },
						transition: { duration: 0.2 }
					}}
				/>
			</AnimatePresence>
			{error && (
				<p aria-live="polite" className={styles["error"]}>
					<Text styledAs="xsr" tag="span">
						{errorMessage}
					</Text>
				</p>
			)}
			{cartQuantity > 0 && (
				<p aria-live="polite" className={styles["success"]}>
					<Icon name="Check" size={14} />
					<Text styledAs="xsr" tag="span">
						{cartQuantity} {alreadyAddedMessage}
					</Text>
				</p>
			)}

			<AddToCartSlider
				isOpen={isSliderOpen}
				onClose={() => setIsSliderOpen(false)}
				onViewCartAndCheckout={handleViewCartAndCheckout}
				onContinueShopping={() => setIsSliderOpen(false)}
			>
				{error ? (
					<Text styledAs="xsr" className="mb-0">
						{errorMessage ?? error}
					</Text>
				) : null}
			</AddToCartSlider>
		</div>
	);
}
